import { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"

/**
 * Seat picker for the one-time lifetime license — CleanShot-style tier grid
 * (1 / 2 / 5 / 10 Macs) with savings vs N × single-Mac price. Confirming a
 * tier hands the product to the regular store checkout.
 */
export default function SeatOptionsModal({ isOpen, onClose, tiers, onSelect }) {
  const texts = TextBundle({ id: "pricing" })
  const { template } = Localization()
  const [selected, setSelected] = useState(null)

  // Preselect the single-Mac tier every time the modal opens
  useEffect(() => {
    if (isOpen) setSelected(tiers?.[0] || null)
  }, [isOpen, tiers])

  if (!tiers?.length) return null
  const baseAmount = tiers[0].amount

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="snap_medium"
      zIndex={500}
      closeOnEscape={true}
      closeOnClickOutside={true}
      title={texts?.seat_modal_title}
      className="md:w-[680px]"
    >
      <Typography
        variant="txt_dim"
        className="block text-center md:text-left mt-1 mb-6"
      >
        {texts?.seat_modal_subtitle}
      </Typography>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-bordered py-8">
        {tiers.map((tier) => {
          const savings = tier.seats * baseAmount - tier.amount
          const isActive = selected?.product?.pid === tier.product?.pid
          return (
            <button
              key={tier.product.pid}
              type="button"
              onClick={() => setSelected(tier)}
              className={`flex flex-col items-center gap-1 rounded-2xl border p-5 transition-all ${
                isActive
                  ? "border border-primary shadow-md bg-primary/5"
                  : "border-bordered hover:border-primary/40"
              }`}
            >
              <span className="text-xs font-semibold tracking-wider text-text_secondary uppercase">
                {tier.seats} {tier.seats === 1 ? texts?.seat_unit_singular : texts?.seat_unit_plural}
              </span>
              <span className="text-2xl font-bold text-text">
                ${tier.amount}
              </span>
              {savings > 0 ? (
                <span className="text-sm font-medium text-white bg-success rounded-full px-2">
                  {template(texts?.seat_savings_note || "Save ${{amount}}", { amount: savings })}
                </span>
              ) : (
                <span className="text-sm text-text_secondary">&ndash;</span>
              )}
            </button>
          )
        })}
      </div>

      <div className="flex justify-end gap-3 pb-2">
        <Button
          variant="secondary"
          onClick={onClose}
        >
          {texts?.seat_modal_cancel_button}
        </Button>
        <Button
          variant="primary"
          disabled={!selected}
          onClick={() => selected && onSelect(selected)}
        >
          {texts?.seat_modal_checkout_button}
        </Button>
      </div>
    </Modal>
  )
}
